// @givmo/app-charity — auth-screen error surfacing.
//
// Every auth screen handles failures the same way: normalise the thrown value through
// `AuthError.from(e)` and branch on `err.kind` (NEVER on a raw Firebase string). A user-initiated
// cancel (`kind === 'cancelled'`) has an empty message and must surface NOTHING. App-Check failures
// arrive as an api-client `GivmoError` thrown out of a `sdk.*` call → `AuthError.from` wraps it as
// `unknown` and RETAINS the PII-safe server message, so they are shown (no silent retry). PII-safe:
// we render only the AuthError's user message; we never log credentials, emails, or tokens.
// (Ported verbatim from the admin app's use-auth-error.)

import { useCallback, useState } from 'react';
import { AuthError } from '@givmo/auth';

export interface UseAuthErrorResult {
  /** The current user-facing error message, or `null` when there is nothing to show. */
  error: string | null;
  /**
   * Whether that error was the company's sign-in lock refusing the sign-in (AS-WEB27): the door then
   * shows the lock notice with the way back in (`SignInDoorError`) instead of the red line.
   */
  lockRefused: boolean;
  /** Clear the error (e.g. on a fresh submit). */
  clearError: () => void;
  /**
   * Normalise a thrown value and store its user message — unless it's a cancellation, which surfaces
   * nothing (and clears any prior error). Returns the resolved {@link AuthError} for callers that
   * need to branch further.
   */
  surface: (thrown: unknown) => AuthError;
}

export function useAuthError(): UseAuthErrorResult {
  const [state, setState] = useState<{ message: string; lockRefused: boolean } | null>(null);

  const clearError = useCallback(() => setState(null), []);

  const surface = useCallback((thrown: unknown): AuthError => {
    const authError = AuthError.from(thrown);
    if (authError.kind === 'cancelled') {
      // A deliberate cancel is not an error to surface (its message is empty).
      setState(null);
    } else {
      setState({
        message:
          authError.message.length > 0 ? authError.message : AuthError.userMessage('unknown'),
        lockRefused: authError.kind === 'organizationSsoRequired',
      });
    }
    return authError;
  }, []);

  return {
    error: state?.message ?? null,
    lockRefused: state?.lockRefused === true,
    clearError,
    surface,
  };
}
